import { Checkbox } from "antd";
import { useEffect, useState } from "react";
import { countryData } from "src/assets/countryData";
import { CustomSelect } from "src/components/CustomSelect";
import styled from "styled-components";
import { CustomInput } from "../../components/CustomInput";
import { StyledButton } from "../../components/StyledButton";
import { SubmitModal } from "../Modal/SubmitModal";

export const RegisterForm = ({ serialNum, partNum }) => {
  const initialFormData = {
    firstName: "",
    surname: "",
    email: "",
    phoneNumber: "",
    state: "",
    city: "",
    postCode: "",
    countryCode: "",
    purchasedFrom: "",
    invoiceNumber: "",
    partNumber: partNum || "",
    serialNumber: serialNum || "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFromSubmitted, setIsFormSubmitted] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isChecked, setIsChecked] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleCountryChange = (input) => {
    // Check if input is empty or null
    if (!input || input.trim().length === 0) {
      setFormData({
        ...formData,
        countryCode: "",
      });
      return;
    }
    //Match country by both name and code
    const matchedCountry = countryData.find(
      (country) =>
        country.code.toLowerCase() === input.toLowerCase() ||
        country.name.toLowerCase() === input.toLowerCase()
    );
    if (matchedCountry) {
      setFormData({
        ...formData,
        countryCode: matchedCountry.code,
      });
    } else {
      setFormData({
        ...formData,
        countryCode: "",
      });
    }
  };

  // Monitor change in partNum & serialNum, when inputPartNumer change, clear serial number, pass the empty serial number here
  useEffect(() => {
    if (partNum) {
      setFormData((prevData) => ({
        ...prevData,
        partNumber: partNum,
      }));
    }

    if (serialNum) {
      setFormData((prevData) => ({
        ...prevData,
        serialNumber: serialNum,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        serialNumber: "",
      }));
    }
  }, [partNum, serialNum]);

  const handleSubmit = async (e) => {
    // Prevent the default form submission
    e.preventDefault();
    // Show loading when submitting form
    setIsLoading(true);

    // Create a payload object with the form data
    try {
      const response = await fetch(process.env.REACT_APP_PRODUCT_REGISTRATION, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      //Stop loading after submit the form
      setIsLoading(false);

      if (result.ErrorFlag === "0") {
        setSuccessMessage("");
        setErrorMessage("");
        setIsModalOpen(true);
      } else {
        setSuccessMessage("");
        setErrorMessage(
          result.ErrorMessage || "Failed to submit the form, please try again."
        );
      }
    } catch (error) {
      setIsLoading(false);
      setSuccessMessage("");
      setErrorMessage(
        "An error occurred while submitting the form, please try again"
      );
    }
  };

  const handleCheck = (e) => {
    setIsChecked(e.target.checked);
  };

  useEffect(() => {
    const {
      firstName,
      surname,
      email,
      phoneNumber,
      state,
      city,
      postCode,
      countryCode,
      purchasedFrom,
      invoiceNumber,
    } = formData;

    const allFieldsFilled =
      firstName &&
      surname &&
      email &&
      phoneNumber &&
      state &&
      city &&
      postCode &&
      countryCode &&
      purchasedFrom &&
      invoiceNumber;

    setIsButtonDisabled(!(allFieldsFilled && isChecked));
  }, [formData, isChecked]);

  // Click OK/Cancel to close modal
  const handleOk = () => {
    setIsModalOpen(false);
    setIsFormSubmitted(true);
    setFormData(initialFormData);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsFormSubmitted(false);
  };

  return (
    <RegisterContainer>
      <h4>Register Your Product</h4>
      {isFromSubmitted && (
        <SubmittedMessage>You have submitted the form</SubmittedMessage>
      )}
      <FormContainer onSubmit={handleSubmit}>
        <InputContainer>
          <CustomInput
            placeholder="First Name *"
            type="text"
            name="firstName"
            required
            value={formData.firstName}
            onChange={handleInputChange}
          />
          <CustomInput
            placeholder="Last Name *"
            type="text"
            name="surname"
            required
            value={formData.surname}
            onChange={handleInputChange}
          />
        </InputContainer>

        <InputContainer>
          <CustomInput
            placeholder="Email Address *"
            type="email"
            name="email"
            required
            value={formData.email}
            onChange={handleInputChange}
          />
          <CustomInput
            placeholder="Contact Phone Number *"
            type="tel"
            name="phoneNumber"
            required
            value={formData.phoneNumber}
            onChange={handleInputChange}
          />
        </InputContainer>

        <InputContainer>
          <CustomInput
            placeholder="State *"
            type="text"
            name="state"
            required
            value={formData.state}
            onChange={handleInputChange}
          />
          <CustomInput
            placeholder="City *"
            type="text"
            name="city"
            required
            value={formData.city}
            onChange={handleInputChange}
          />
        </InputContainer>

        <InputContainer>
          <CustomInput
            placeholder="Zip / Post Code *"
            type="text"
            name="postCode"
            required
            value={formData.postCode}
            onChange={handleInputChange}
          />
          <CustomSelect
            value={formData.countryCode}
            onChange={handleCountryChange}
            placeholder="Country *"
            options={countryData}
            required
          />
        </InputContainer>

        <InputContainer>
          <CustomInput
            placeholder="Purchased From *"
            type="text"
            name="purchasedFrom"
            required
            value={formData.purchasedFrom}
            onChange={handleInputChange}
          />
          <CustomInput
            placeholder="Invoice Number *"
            type="text"
            name="invoiceNumber"
            required
            value={formData.invoiceNumber}
            onChange={handleInputChange}
          />
        </InputContainer>

        <InputContainer>
          <CustomInput
            placeholder="Part Number *"
            type="text"
            name="partNumber"
            value={
              partNum
                ? `Part number: ${formData.partNumber}`
                : formData.partNumber
            }
            disabled
            required
          />
          <CustomInput
            placeholder="Serial Number"
            type="text"
            name="serialNumber"
            value={
              serialNum
                ? `Serial number: ${formData.serialNumber}`
                : formData.serialNumber || ""
            }
            onChange={handleInputChange}
            disabled={!!serialNum} // Disabled input field when there is serialNum
          />
        </InputContainer>

        {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
        {errorMessage && <p className="error-msg">{errorMessage}</p>}

        <StyledCheckbox onChange={handleCheck} required>
          By submitting this form, I agree to &nbsp;
          <StyledTextLink
            href="https://store.arb.com.au/pages/privacy-policy-terms-of-use"
            target="_blank"
          >
            ARB's Privacy Policy and Disclaimer
          </StyledTextLink>
          .
        </StyledCheckbox>
        <StyledButton
          className="medium"
          type="submit"
          disabled={isButtonDisabled || isLoading}
        >
          {isLoading ? "Submitting ..." : "Submit"}
        </StyledButton>
      </FormContainer>

      <SubmitModal
        isVisible={isModalOpen}
        title="Submission Successful"
        onOk={handleOk}
        onCancel={handleCancel}
        message="You have successfully registered your products."
      />
    </RegisterContainer>
  );
};

const RegisterContainer = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 10px;
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 20px;
  transition: min-height 0.3s ease-in-out;

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;

  @media (max-width: 600px) {
    flex-direction: column;
  }
  @media (min-width: 601px) and (max-width: 1023px) {
    flex-direction: column;
  }
`;

const SuccessMessage = styled.p`
  color: green;
  font-size: 14px;
  margin-bottom: 10px;
`;

const SubmittedMessage = styled.p`
  color: var(--arb-red);
  font-size: var(--body-small-font-size);
`;

const StyledTextLink = styled.a`
  font-size: var(--body-font-size);
  font-family: var(--font-family);
  color: var(--text-link);
  text-decoration: underline;
  &:hover {
    color: var(--text-link-hover);
    text-decoration: underline;
  }
  &:focus {
    color: var(--arb-red);
    text-decoration: underline;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  &.ant-checkbox-wrapper {
    font-size: var(--body-font-size);
    font-family: var(--font-family);
    line-height: 1.5;
    box-shadow: none !important;
  }

  /* Priority hover and focus */
  .ant-checkbox-wrapper:hover .ant-checkbox-inner,
  .ant-checkbox:hover .ant-checkbox-inner {
    border-color: var(--grey-80);
    background-color: var(--grey-80);
    transition: border-color 0.2s ease, background-color 0.2s ease;
  }

  /* Keep hover when checkbox in both hover and focus */
  .ant-checkbox-wrapper:hover .ant-checkbox-inner:focus,
  .ant-checkbox:hover .ant-checkbox-inner:focus {
    border-color: var(--grey-80);
    background-color: var(--grey-80);
    transition: border-color 0.2s ease, background-color 0.2s ease;
  }

  /* Focus */
  .ant-checkbox-input:focus + .ant-checkbox-inner {
    background-color: var(--black);
    border: 2px solid var(--old-man-emu-yellow);
    transition: border-color 0.2s ease, background-color 0.2s ease;
  }

  .ant-checkbox-inner {
    width: var(--icon-size-small);
    height: var(--icon-size-small);
    border-color: var(--black);

    &::after {
      width: 8px;
      height: 12px;
    }
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: var(--black) !important;
    transition: border-color 0.2s ease, background-color 0.2s ease;
  }
`;
